/**
 * Enricher that adds interactive checklist controls in the preview.
 * - Headings (h2-h6) get an inline checkbox and section highlight.
 * - Markdown task-list items (- [ ] / - [x]) become interactive and persist state.
 *
 * Runs with lowest priority (10) to ensure all other enrichers have processed first.
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';

export interface ChecklistEnricherConfig extends EnricherConfig {
  /** Returns the currently checked heading slugs */
  getCheckedSlugs: () => string[];
  /** Called when a checkbox is toggled */
  onToggle: (slug: string, checked: boolean) => void;
}

export class ChecklistEnricher extends BaseEnricher {
  private getCheckedSlugs: () => string[];
  private onToggle: (slug: string, checked: boolean) => void;
  private enabled = false;

  constructor(config: ChecklistEnricherConfig) {
    super({ priority: 10, ...config });
    this.getCheckedSlugs = config.getCheckedSlugs;
    this.onToggle = config.onToggle;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  shouldRun(_context: EnricherContext): boolean {
    return this.enabled;
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    const checkedSlugs = new Set(this.getCheckedSlugs());
    // Skip document title (h1); checklist is meant for actionable sections.
    const headings = Array.from(container.querySelectorAll('h2, h3, h4, h5, h6')) as HTMLElement[];

    // Track used slugs for deduplication
    const usedSlugs = new Map<string, number>();

    for (const heading of headings) {
      const slug = this.getUniqueSlug(heading, usedSlugs, 'heading');

      // Create section wrapper
      const sectionDiv = document.createElement('div');
      sectionDiv.className = 'checklist-section';
      sectionDiv.dataset.headingId = slug;

      if (checkedSlugs.has(slug)) {
        sectionDiv.classList.add('checked');
      }

      // Insert wrapper before the heading
      heading.parentNode!.insertBefore(sectionDiv, heading);

      // Move heading into wrapper
      sectionDiv.appendChild(heading);

      // Move all following siblings until the next heading (any level)
      let sibling = sectionDiv.nextSibling;
      while (sibling) {
        const next = sibling.nextSibling;
        if (sibling.nodeType === Node.ELEMENT_NODE) {
          const el = sibling as HTMLElement;
          if (/^H[1-6]$/i.test(el.tagName)) break;
        }
        sectionDiv.appendChild(sibling);
        sibling = next;
      }

      // Create checkbox
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'checklist-checkbox';
      checkbox.checked = checkedSlugs.has(slug);
      checkbox.title = 'Mark this step as completed';

      // Prevent checkbox click from bubbling to heading click handler
      checkbox.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      const toggleCheck = () => {
        checkbox.checked = !checkbox.checked;
        if (checkbox.checked) {
          sectionDiv.classList.add('checked');
        } else {
          sectionDiv.classList.remove('checked');
        }
        this.onToggle(slug, checkbox.checked);
      };

      checkbox.addEventListener('change', () => {
        // Checkbox already toggled by native behavior, just sync state
        if (checkbox.checked) {
          sectionDiv.classList.add('checked');
        } else {
          sectionDiv.classList.remove('checked');
        }
        this.onToggle(slug, checkbox.checked);
      });

      // Make the entire heading clickable to toggle the checkbox
      heading.style.cursor = 'pointer';
      heading.addEventListener('click', (e) => {
        // Don't toggle if user clicked the checkbox itself (handled by change event)
        if (e.target === checkbox) return;
        // Don't toggle if user clicked a link inside the heading
        if ((e.target as HTMLElement).closest('a')) return;
        toggleCheck();
      });

      // Prepend checkbox to heading
      heading.insertBefore(checkbox, heading.firstChild);
    }

    this.enrichTaskListItems(container, checkedSlugs, usedSlugs);
  }

  /**
   * Generate a unique slug for a heading.
   * Reuses existing id if one was set (e.g., by TocEnricher).
   * Handles duplicate headings by appending -2, -3, etc.
   */
  private getUniqueSlug(
    element: Element,
    usedSlugs: Map<string, number>,
    prefix: 'heading' | 'task'
  ): string {
    let baseSlug: string;

    if (prefix === 'heading' && element.id) {
      baseSlug = element.id;
    } else {
      const text = element.textContent?.trim() ?? '';
      const textSlug = this.slugify(text);
      baseSlug = prefix === 'task' ? `task-${textSlug || 'item'}` : textSlug;
    }

    if (!baseSlug) {
      baseSlug = prefix;
    }

    const count = usedSlugs.get(baseSlug) ?? 0;
    usedSlugs.set(baseSlug, count + 1);

    const slug = count === 0 ? baseSlug : `${baseSlug}-${count + 1}`;
    if (prefix === 'heading') {
      element.id = slug;
    }
    return slug;
  }

  private enrichTaskListItems(
    container: HTMLElement,
    checkedSlugs: Set<string>,
    usedSlugs: Map<string, number>
  ): void {
    // marked task lists render as <li><input type="checkbox" disabled ...> text</li>
    const taskInputs = Array.from(
      container.querySelectorAll('li > input[type="checkbox"][disabled], li > p > input[type="checkbox"][disabled]')
    ) as HTMLInputElement[];

    for (const input of taskInputs) {
      const taskItem = input.closest('li');
      if (!taskItem) continue;
      const taskList = taskItem.parentElement as HTMLElement | null;
      if (taskList && /^(UL|OL)$/i.test(taskList.tagName)) {
        taskList.classList.add('checklist-task-list');
      }

      const slug = this.getUniqueSlug(taskItem, usedSlugs, 'task');
      const isChecked = checkedSlugs.has(slug);

      taskItem.classList.add('checklist-task-item');
      taskItem.dataset.checklistTaskId = slug;
      taskItem.classList.toggle('checked', isChecked);

      input.classList.add('checklist-checkbox');
      input.disabled = false;
      input.checked = isChecked;
      input.title = 'Mark this checklist item as completed';

      input.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      input.addEventListener('change', () => {
        taskItem.classList.toggle('checked', input.checked);
        this.onToggle(slug, input.checked);
      });

      taskItem.style.cursor = 'pointer';
      taskItem.addEventListener('click', (e) => {
        if (e.target === input) return;
        if ((e.target as HTMLElement).closest('a')) return;
        input.checked = !input.checked;
        taskItem.classList.toggle('checked', input.checked);
        this.onToggle(slug, input.checked);
      });
    }
  }

  private slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
}
